/**
 * Gemeinsames Rendering für E-Mail-Signatur-Platzhalter.
 *
 * Wird vom Signatur-Editor (Vorschau), den Organisations-Einstellungen und
 * dem E-Mail-Composer (Versand) benutzt, damit überall dieselben Regeln
 * gelten — insbesondere: leere Werte hinterlassen keine Fragmente wie
 * "Tel:  |" in der fertigen Signatur (Kundenfeedback 19.08.26).
 */

/** Alle unterstützten Platzhalter-Schlüssel (ohne geschweifte Klammern). */
const PLACEHOLDER_KEYS = [
  "vorname",
  "nachname",
  "titel",
  // Stellenbezeichnung des Mitarbeiters ("Unfallsachbearbeiter") — bewusst
  // getrennt von "titel" (akademischer Titel) und von der Benutzerrolle,
  // die nur Rechte im System steuert (Kundenfeedback 10.09.26).
  "stellenbezeichnung",
  "email",
  "mobil",
  "festnetz",
  "benutzername",
  "organisation",
  "webseite",
  "homepage",
  "standort",
  "standort_adresse",
  "standort_telefon",
  "standort_email",
] as const;

export type SignaturePlaceholderKey = (typeof PLACEHOLDER_KEYS)[number];

export type SignatureValues = Partial<Record<SignaturePlaceholderKey, string | null | undefined>>;

const KEY_PATTERN = PLACEHOLDER_KEYS.join("|");

/** Platzhalter in Textknoten — HTML-Tags werden separat behandelt. */
const TEXT_TOKEN_RE = new RegExp(`(<[^>]*>)|\\{\\{(${KEY_PATTERN})\\}\\}`, "g");

/** Platzhalter innerhalb eines einzelnen Tags (z. B. href="mailto:{{email}}"). */
const ATTR_TOKEN_RE = new RegExp(`\\{\\{(${KEY_PATTERN})\\}\\}`, "g");

/**
 * Marker für einen leer aufgelösten Platzhalter. Bleibt bis zur Bereinigung
 * im Zwischenergebnis stehen, damit Beschriftungen ("Tel:") und Trennzeichen
 * (" | ") neben leeren Werten mit entfernt werden können.
 */
const EMPTY_MARK = "\u0000";

/**
 * Liefert "" für Telefonwerte, die nur aus einer Ländervorwahl bestehen
 * (z. B. "+49", "+49 ") oder leer sind — es wurde also nichts Echtes
 * eingetragen. Alles mit mindestens einer Ziffer nach der Vorwahl gilt
 * als gültige Nummer.
 */
export function normalizePhone(value?: string | null): string {
  const s = (value ?? "").trim();
  if (!s) return "";
  if (/^\+\d{1,3}\s*$/.test(s)) return "";
  return s;
}

/**
 * Ersetzt alle Signatur-Platzhalter in `html` durch die übergebenen Werte
 * und räumt danach verwaiste Beschriftungen, Trennzeichen und leere Zeilen
 * weg. `wrapValue` kann Werte für die Vorschau dekorieren (Hervorhebung);
 * innerhalb von HTML-Attributen wird immer der nackte Wert eingesetzt.
 */
export function renderSignatureHtml(
  html: string,
  values: SignatureValues,
  wrapValue: (value: string, key: SignaturePlaceholderKey) => string = (v) => v,
): string {
  if (!html) return "";

  const resolve = (key: SignaturePlaceholderKey): string => (values[key] ?? "").trim();

  // 1) Platzhalter in Attributen (href="mailto:{{email}}" …) nackt ersetzen.
  let out = html.replace(/<[^>]*>/g, (tag) =>
    tag.replace(ATTR_TOKEN_RE, (_m, key: SignaturePlaceholderKey) => resolve(key)),
  );

  // 2) Platzhalter im Text ersetzen; leere Werte hinterlassen einen Marker.
  out = out.replace(TEXT_TOKEN_RE, (_m, tag: string, key: SignaturePlaceholderKey) => {
    if (tag) return tag;
    const value = resolve(key);
    return value ? wrapValue(value, key) : EMPTY_MARK;
  });

  return cleanupEmptySegments(out);
}

/**
 * Entfernt einen Platzhalter (z. B. "{{mobil}}") aus einer Signatur-Vorlage
 * und räumt dabei dieselben Reste weg wie beim Rendern eines leeren Werts:
 * Beschriftung ("Mobil:"), Trennzeichen (" | ") und Zeilen, die nur aus
 * dem Platzhalter bestanden. Ohne diese Bereinigung blieb nach dem Abwählen
 * eines Chips im Editor ein Fragment wie "Tel: +49 … | Mobil:" stehen
 * (Kundenfeedback 11.09.26). Alle übrigen Platzhalter bleiben unverändert.
 */
export function removePlaceholderFromHtml(html: string, placeholder: string): string {
  if (!html || !placeholder || !html.includes(placeholder)) return html;
  return cleanupEmptySegments(html.split(placeholder).join(EMPTY_MARK));
}

const GAP = String.raw`(?:&nbsp;|\s)*`;
const SEPARATOR = `${GAP}(?:\\||·|•|–)${GAP}`;

/**
 * Entfernt Beschriftungen, Trennzeichen und Zeilen, die nach der Ersetzung
 * nur noch leere Platzhalter enthalten — z. B. wird aus
 * "Tel: ␀ | Mobil: +49 170 …" sauber "Mobil: +49 170 …".
 */
function cleanupEmptySegments(html: string): string {
  let out = html;

  // Beschriftung direkt vor einem leeren Wert entfernen ("Tel:", "Mobil:" …).
  out = out.replace(
    new RegExp(`(?:Tel\\.?|Telefon|Mobil|Fax|Fon|E-?Mail|Web(?:seite)?)\\s*:?${GAP}${EMPTY_MARK}`, "gi"),
    EMPTY_MARK,
  );

  // Trennzeichen neben leeren Werten einsammeln (" | ␀", "␀ | ").
  out = out.replace(new RegExp(`${SEPARATOR}${EMPTY_MARK}`, "g"), EMPTY_MARK);
  out = out.replace(new RegExp(`${EMPTY_MARK}${SEPARATOR}`, "g"), EMPTY_MARK);
  out = out.replace(new RegExp(`${EMPTY_MARK}(?:${GAP}${EMPTY_MARK})+`, "g"), EMPTY_MARK);

  // <div>-Zeilen, die nur noch aus leeren Werten bestehen, komplett entfernen.
  out = out.replace(new RegExp(`<div[^>]*>(?:&nbsp;|\\s|${EMPTY_MARK})*</div>\\s*`, "gi"), "");

  // <br>-Zeilen: Zeilen, die durch leere Werte inhaltslos wurden, samt
  // Zeilenumbruch entfernen (bewusst leere Vorlagen-Zeilen bleiben stehen).
  out = removeEmptyBrLines(out);

  return out.replace(new RegExp(EMPTY_MARK, "g"), "");
}

function removeEmptyBrLines(html: string): string {
  const parts = html.split(/(<br\s*\/?>)/i);
  const kept: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i] ?? "";
    if (/^<br\s*\/?>$/i.test(part)) {
      kept.push(part);
      continue;
    }
    const hadEmptyToken = part.includes(EMPTY_MARK);
    const visible = part
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, "")
      .replace(new RegExp(EMPTY_MARK, "g"), "")
      .trim();
    if (hadEmptyToken && !visible) {
      // Zeile ist nur wegen leerer Platzhalter leer → Folge-<br> mitnehmen.
      if (/^<br\s*\/?>$/i.test(parts[i + 1] ?? "")) i++;
      continue;
    }
    kept.push(part);
  }
  return kept.join("");
}

/**
 * Signatur als Block, wie er in einen Editor eingefügt wird.
 *
 * data-signature="true" markiert den Block. Darauf stützen sich zwei Dinge:
 * der Austausch der Signatur bei einem Standortwechsel und die Reihenfolge
 * in einer Antwort — die Signatur schließt den eigenen Text ab und steht
 * damit VOR dem zitierten Verlauf (Kundenfeedback 14.09.26).
 */
export function signatureBlockHtml(signature: string): string {
  if (!signature) return "";
  return `<br><br><div class="mail-signature" data-signature="true">${signature}</div>`;
}

/** Anzeigegröße des Fotos in der Signatur (rund). */
export const SIGNATURE_PHOTO_SIZE = 72;

/**
 * Setzt das Mitarbeiterfoto links neben die Signatur (Kundenfeedback
 * 16.10.2026 Teil 2, XXXII). Tabelle statt Flexbox, weil Mailprogramme
 * (Outlook) nur damit zuverlässig nebeneinander setzen; das Bild kommt per
 * URL (kein Base64), damit die Mail nicht wächst. Ohne URL bleibt die
 * Signatur unverändert.
 */
export function withSignaturePhoto(signatureHtml: string, photoUrl: string | null | undefined): string {
  const url = (photoUrl ?? "").trim();
  if (!signatureHtml || !url) return signatureHtml;
  const safeUrl = url.replace(/"/g, "&quot;");
  const px = SIGNATURE_PHOTO_SIZE;
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" data-signature-photo="true" style="border-collapse:collapse;">` +
    `<tr>` +
    `<td valign="top" style="padding:0 14px 0 0;vertical-align:top;">` +
    `<img src="${safeUrl}" width="${px}" height="${px}" alt="" ` +
    `style="display:block;width:${px}px;height:${px}px;border-radius:50%;object-fit:cover;border:0;">` +
    `</td>` +
    `<td valign="top" style="vertical-align:top;">${signatureHtml}</td>` +
    `</tr>` +
    `</table>`
  );
}

/**
 * Zitierter E-Mail-Verkehr als Block, wie er beim Antworten und
 * Weiterleiten in den Editor kommt.
 *
 * data-quoted="true" markiert den Block, damit eine nachträglich gesetzte
 * Signatur ihn findet und sich DAVOR einfügt statt darunter — sonst steht
 * die eigene Signatur unter dem gesamten Verlauf (Kundenfeedback 14.09.26).
 */
export function quotedHistoryBlockHtml(quotedHtml: string): string {
  if (!quotedHtml) return "";
  return `<br><br><div class="mail-quoted" data-quoted="true">${quotedHtml}</div>`;
}
